  /*
 * Copyright (c) 2025 MacTrack. All rights reserved.
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from './supabaseClient';
import { AppLayout } from './components/layout/AppLayout';
import { Header } from './components/layout/Header';
import { Sidebar } from './components/layout/Sidebar';
import { BottomNavigation, NavigationItem } from './components/navigation/BottomNavigation';
import { ViewModeSelector } from './components/navigation/ViewModeSelector';

import MealDashboard from './MealDashboard';
import ClientProfilePanel from './ClientProfilePanel';
import ClientAnalytics from './ClientAnalytics';
import StreakTracker from './StreakTracker';
import ClientLeaderboard from './ClientLeaderboard';
import DietGoalCalculator from './DietGoalCalculator';
import GoalPerformanceInsights from './GoalPerformanceInsights';
import GoalTrackingChart from './GoalTrackingChart';
import DashboardOverview from './DashboardOverview';
import AdvancedMealAnalysis from './components/analytics/AdvancedMealAnalysis';

import { BetaAccessManager } from './components/admin/BetaAccessManager';
import SubscriptionPlans from './components/subscription/SubscriptionPlans';
import ReferralRetentionSystem from './components/rewards/ReferralRetentionSystem';
import DataExportSystem from './components/reports/DataExportSystem';
import { PerformanceMonitor } from './components/ui/PerformanceMonitor';
import { AuthForm } from './components/auth/AuthForm';
import { FloatingNotificationBell } from './components/ui/FloatingNotificationBell';

import { ClientsView } from './components/client/ClientsView';
import { MealLogsView } from './components/meals/MealLogsView';
import { TutorialOverlay } from './components/tutorial/TutorialOverlay';
import { TutorialService } from './services/tutorialService';
import { TutorialTest } from './components/tutorial/TutorialTest';
import './App.css';
import { PlanProvider } from './context/PlanContext';


// Wrapper component for ClientAnalytics that fetches data
function ClientAnalyticsWrapper({ 
  selectedUserId, 
  clientName, 
  onDataLoaded 
}: { 
  selectedUserId: string; 
  clientName: string;
  onDataLoaded?: (data: any[], goals: any) => void;
}) {
  const [clientData, setClientData] = useState<any[]>([]);
  const [calculatedGoals, setCalculatedGoals] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  const fetchClientData = useCallback(async () => {
    try {
      setLoading(true);
      // Fetch meal data
      const { data: mealData, error: mealError } = await supabase
        .from('Mastersheet')
        .select('*')
        .eq('telegram_user_id', selectedUserId)
        .order('created_at', { ascending: false });

      if (mealError) {
        console.error('Error fetching client data:', mealError);
        return;
      }



      // Fetch diet goals
      const { data: goalsData, error: goalsError } = await supabase
        .from('client_diet_goals')
        .select('*')
        .eq('telegram_user_id', selectedUserId)
        .single();

      if (goalsError && goalsError.code !== 'PGRST116') { // PGRST116 = no rows returned
        console.error('Error fetching diet goals:', goalsError);
      }

      setClientData(mealData || []);
      
      // Format diet goals for the analytics component
      let formattedGoals = null;
      if (goalsData) {
        formattedGoals = {
          targetCalories: goalsData.target_calories,
          protein: { 
            min: goalsData.protein_min, 
            max: goalsData.protein_max, 
            target: goalsData.protein_target 
          },
          carbs: { 
            min: goalsData.carbs_min, 
            max: goalsData.carbs_max, 
            target: goalsData.carbs_target 
          },
          fat: { 
            min: goalsData.fat_min, 
            max: goalsData.fat_max, 
            target: goalsData.fat_target 
          }
        };
        setCalculatedGoals(formattedGoals);
      } else {
        setCalculatedGoals(null);
      }

      // Notify parent component of loaded data
      if (onDataLoaded) {
        onDataLoaded(mealData || [], formattedGoals);
      }
    } catch (error) {
      console.error('Error fetching client data:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedUserId]);

  useEffect(() => {
    if (selectedUserId) {
      fetchClientData();
    }
  }, [selectedUserId, fetchClientData]);

  if (loading) {
    return (
      <div className="space-y-6 pb-20 md:pb-6">
        <div className="retro-card p-4 md:p-6">
          <div className="text-center py-8">
            <div className="loading mx-auto mb-4"></div>
            <p className="text-text-medium">Loading analytics...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-20 md:pb-6">
      <ClientAnalytics 
        clientData={clientData}
        clientName={clientName}
        calculatedGoals={calculatedGoals}
      />
    </div>
  );
}

// Trends view component that combines analytics and export functionality
function TrendsView({ selectedUserId, selectedClientName }: { selectedUserId: string; selectedClientName: string }) {
  const [clientData, setClientData] = useState<any[]>([]);
  const [calculatedGoals, setCalculatedGoals] = useState<any>(null);

  const handleDataLoaded = useCallback((data: any[], goals: any) => {
    setClientData(data);
    setCalculatedGoals(goals);
  }, []);



  // Show message if no client is selected
  if (!selectedUserId) {
    return (
      <div className="space-y-6 pb-20 md:pb-6">
        <div className="retro-card p-4 md:p-6">
          <div className="text-center py-8">
            <h3 className="text-lg font-semibold text-gray-800 mb-2">📊 Analytics</h3>
            <p className="text-gray-600">Please select a client first to view their analytics and trends.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-20 md:pb-6">
      <ClientAnalyticsWrapper 
        selectedUserId={selectedUserId}
        clientName={selectedClientName}
        onDataLoaded={handleDataLoaded}
      />
      <DataExportSystem 
        selectedUserId={selectedUserId}
        clientName={selectedClientName}
        clientData={clientData}
        calculatedGoals={calculatedGoals}
      />
    </div>
  );
}

// Register service worker for PWA (opt-in via env to avoid stale cache issues)
if (process.env.REACT_APP_ENABLE_SW === 'true' && 'serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js')
      .then((registration) => {
        console.log('SW registered: ', registration);
      })
      .catch((registrationError) => {
        console.log('SW registration failed: ', registrationError);
      });
  });
}



function App() {
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedClientName, setSelectedClientName] = useState('');
  const [currentView, setCurrentView] = useState<NavigationItem>('overview');
  const [user, setUser] = useState<any>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [authMode, setAuthMode] = useState<'login' | 'register'>('login');
  const [authFormLoading, setAuthFormLoading] = useState(false);
  const [message, setMessage] = useState<{type: 'success' | 'error', text: string} | null>(null);
  const [emailConfirmationSent, setEmailConfirmationSent] = useState(false);
  const [refreshTrigger] = useState(0);
  const [showTutorial, setShowTutorial] = useState(false);
  const [tutorialStep, setTutorialStep] = useState(1);

  // Authentication setup
  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      setAuthLoading(false);
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Check if tutorial should be shown
  useEffect(() => {
    const checkTutorialStatus = async () => {
      if (user) {
        const shouldShow = await TutorialService.shouldShowTutorial();
        setShowTutorial(shouldShow);
      }
    };

    checkTutorialStatus();
  }, [user]);

  const handleAuth = async (email: string, password: string, name?: string, inviteCode?: string) => {
    setAuthFormLoading(true);
    try {
      if (authMode === 'register') {
        // Validate invite code first
        if (inviteCode) {
          // Add debugging
          console.log('🔍 Debugging beta code validation:');
          console.log('Invite code:', inviteCode);
          console.log('Invite code type:', typeof inviteCode);
          console.log('Invite code length:', inviteCode?.length);

          const { data: isValidCode, error: codeError } = await supabase
            .rpc('validate_beta_invite_code', { invite_code: inviteCode });
          
          console.log(' Validation result:');
          console.log('isValidCode:', isValidCode);
          console.log('codeError:', codeError);
          
          if (codeError || !isValidCode) {
            throw new Error('Invalid or expired beta invite code. Please contact support for a valid code.');
          }
        }

        // Register new user
        const { data: signUpData, error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            data: {
              full_name: name,
              role: 'trainer'
            }
          }
        });

        if (error) throw error;

        // Use the invite code for the new user
        if (signUpData.user && inviteCode) {
          const { error: useCodeError } = await supabase
            .rpc('use_beta_invite_code', { 
              invite_code: inviteCode, 
              user_uuid: signUpData.user.id 
            });
          
          if (useCodeError) {
            console.error('Error using invite code:', useCodeError);
            // Don't throw error here as user is already created
          }
        }

        setEmailConfirmationSent(true);
        setMessage({ 
          type: 'success', 
          text: 'Account created successfully! Please check your email and click the confirmation link before logging in.' 
        });
        setAuthMode('login');
        
        // Don't try to login immediately - user needs to confirm email first
        return;
      } else {
        // Login existing user
        const { error } = await supabase.auth.signInWithPassword({
          email,
          password
        });

        if (error) {
          // Handle email confirmation error specifically
          if (error.message.includes('Email not confirmed')) {
            setMessage({ 
              type: 'error', 
              text: 'Please check your email and click the confirmation link before logging in.' 
            });
            return;
          }
          throw error;
        }
      }
    } catch (error: any) {
      console.error('Auth error:', error);
      setMessage({ 
        type: 'error', 
        text: error.message || 'Authentication failed. Please try again.' 
      });
    } finally {
      setAuthFormLoading(false);
    }
  };

  const handleSignOut = () => {
    supabase.auth.signOut();
  };

  const handleClientSelect = (userId: string, clientName: string) => {
    setSelectedUserId(userId);
    setSelectedClientName(clientName);
  };

  const handleNavigate = (view: NavigationItem) => {
    setCurrentView(view);
  };

  const handleTutorialComplete = () => {
    setShowTutorial(false);
    setTutorialStep(1);
  };

  const handleTutorialSkip = () => {
    setShowTutorial(false);
    setTutorialStep(1);
  };

  const handleTutorialStepChange = (step: number) => {
    setTutorialStep(step);
  };

  // Clear message after 3 seconds
  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => setMessage(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  // Show loading or auth required message
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="loading"></div>
        <span className="ml-3 text-text-medium">Loading...</span>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-paper-cream">
        <AuthForm 
          onSubmit={handleAuth} 
          loading={authFormLoading}
          mode={authMode}
          onModeChange={setAuthMode}
          errorMessage={message?.type === 'error' ? message.text : undefined}
        />
      </div>
    );
  }

  return (
    <PlanProvider>
      <div className="min-h-screen bg-paper-white">
      {/* Performance Monitor - Only in development */}
      {process.env.NODE_ENV === 'development' && (
        <PerformanceMonitor />
      )}
      
      {/* Tutorial Test floating tool moved into header (inline). Keep fallback hidden. */}
      
      {/* Floating Notification Bell - Available on all views */}
      <FloatingNotificationBell />
      
      {/* Tutorial Overlay */}
      <TutorialOverlay
        isVisible={showTutorial}
        onComplete={handleTutorialComplete}
        onSkip={handleTutorialSkip}
        currentStep={tutorialStep}
        onStepChange={handleTutorialStepChange}
      />
      
      <AppLayout message={message} onSignOut={handleSignOut}>
        {/* Content Area */}
        {currentView === 'overview' && (
          <DashboardOverview 
            onClientSelect={handleClientSelect}
            selectedUserId={selectedUserId}
            onNavigate={handleNavigate}
          />
        )}

        {currentView === 'clients' && (
          <ClientsView
            onClientSelect={handleClientSelect}
            selectedUserId={selectedUserId}
            refreshTrigger={refreshTrigger}
            onNavigate={handleNavigate}
          />
        )}

        {selectedUserId && currentView === 'meals' && (
          <MealLogsView 
            selectedUserId={selectedUserId}
            clientName={selectedClientName}
          />
        )}

        {selectedUserId && currentView === 'insights' && (
          <GoalPerformanceInsights 
            selectedUserId={selectedUserId}
            clientName={selectedClientName}
            onSwitchToProfile={() => setCurrentView('overview')}
            onSwitchToGoals={() => setCurrentView('overview')}
            onUpgrade={() => handleNavigate('subscription')}
          />
        )}

        {currentView === 'leaderboard' && (
          <ClientLeaderboard />
        )}

        {selectedUserId && currentView === 'diet' && (
          <div className="space-y-6 pb-20 md:pb-6">
            <DietGoalCalculator 
              selectedUserId={selectedUserId}
              onGoalsCalculated={() => {
                // Show success message instead of navigating away
                setMessage({ 
                  type: 'success', 
                  text: 'Diet goals updated successfully! 🎯' 
                });
              }}
              onUpgrade={() => handleNavigate('subscription')}
            />
            

          </div>
        )}

        {selectedUserId && currentView === 'trends' && (
          <TrendsView 
            selectedUserId={selectedUserId}
            selectedClientName={selectedClientName}
          />
        )}

        {currentView === 'subscription' && (
          <SubscriptionPlans />
        )}

        {/* Bottom Navigation */}
        <BottomNavigation
          currentView={currentView}
          onNavigate={handleNavigate}
          selectedUserId={selectedUserId}
        />
        </AppLayout>
      </div>
    </PlanProvider>
  );
}

export default App;
